import { z } from 'zod';
import {
  BrandMode,
  CountryCode,
  Email,
  HexColor,
  Id,
  InvoicingDraftKind,
  LongText,
  Phone,
  Money,
  ShortText,
  StorageDayType,
  StorageStartRule,
  TaxMode,
  VatRate,
  amount,
} from './common.js';
import { OpeningHoursSchedule } from '../opening-hours.js';
import { LocationKind } from './agenda.js';
import { CustomerPhotoNotice, DossierRetentionYears } from './privacy.js';
import { EntryChecklist, EntryChecklistItem } from './reception.js';

/** Marque du garage : appliquée à l'espace garage, au portail et aux e-mails. */
export const BrandingOutput = z.object({
  displayName: z.string(),
  primaryColor: HexColor,
  secondaryColor: HexColor,
  senderDisplayName: z.string(),
  replyToEmail: z.string().nullable(),
  /** URL absolue du logo (servie par l'API, chemin non devinable), ou `null`. */
  logoUrl: z.string().nullable(),
  /**
   * Vague 5 : URL absolue de l'icône carrée (512 px), distincte du logo : barre latérale, en-tête mobile,
   * favicon, application installée. `null` : initiales du nom affiché.
   */
  iconUrl: z.string().nullable(),
  /** Coordonnées affichées au client (signature des e-mails, portail, « Véhicule prêt »). */
  address: z.string().nullable(),
  phone: z.string().nullable(),
  openingHours: z.string().nullable(),
  /** Vague 6 : 3e couleur de marque (ex. argent), ou `null` ; mode de présence de la marque. */
  tertiaryColor: HexColor.nullable(),
  brandMode: BrandMode,
  /** Vague 6 : raison sociale du garage (politique de confidentialité), ou `null`. */
  legalName: z.string().nullable(),
  /**
   * Vague 6 : contact « données personnelles » du garage, publié par la politique de confidentialité. À
   * défaut, la politique affiche le contact de l'éditeur, jamais `replyToEmail` (audit S40).
   */
  privacyContactEmail: z.string().nullable(),
  /**
   * Vague 7 : horaires structurés (jours et plages, fuseau du garage), en plus du texte libre
   * `openingHours` ; servent au bloc contact du portail (« Ouvert jusqu'à 18 h »). `null` : non réglés.
   */
  openingHoursSchedule: OpeningHoursSchedule.nullable(),
  /**
   * 02/10/2026 : `true` quand une apparence **propre à la session de démonstration** est appliquée (bouton
   * « Revenir à l'apparence d'origine ») ; absent hors démonstration ou sans remplacement.
   */
  demoOverride: z.literal(true).optional(),
});
export type BrandingOutput = z.infer<typeof BrandingOutput>;

export const BrandingUpdateInput = z.strictObject({
  displayName: ShortText.optional(),
  primaryColor: HexColor.optional(),
  secondaryColor: HexColor.optional(),
  senderDisplayName: ShortText.optional(),
  replyToEmail: Email.nullable().optional(),
  address: LongText.max(300).nullable().optional(),
  phone: Phone.nullable().optional(),
  openingHours: LongText.max(300).nullable().optional(),
  legalName: ShortText.nullable().optional(),
  privacyContactEmail: Email.nullable().optional(),
  tertiaryColor: HexColor.nullable().optional(),
  brandMode: BrandMode.optional(),
  /** Vague 7 : `null` efface les horaires structurés. */
  openingHoursSchedule: OpeningHoursSchedule.nullable().optional(),
});

export const SettingsOutput = z.object({
  storageFreeDays: z.int(),
  storageDayType: StorageDayType,
  storageStartRule: StorageStartRule,
  storageDailyFee: amount(),
  storageFeeTaxMode: TaxMode,
  storageTermsText: z.string(),
  invoicingDraftKind: InvoicingDraftKind,
  timezone: z.string(),
  defaultVatRate: VatRate,
  portalTokenTtlDays: z.int(),
  /** Durée d'accès au portail après la sortie du véhicule (heures). */
  portalAccessAfterReleaseHours: z.int(),
  /** Validité du lien du compte rendu permanent remis à la sortie (jours). */
  reportLinkTtlDays: z.int(),
  notificationEmails: z.array(z.string()),
  /** Pays par défaut des formulaires (indicatif téléphonique), ISO 2 lettres (vague 4). */
  countryCode: z.string(),
  /** Photo du véhicule dans l'en-tête du portail (vague 4) ; désactivé par défaut (plaque visible). */
  showVehiclePhotoOnPortal: z.boolean(),
  /**
   * Vague 6 : texte d'information lu au client avant de prendre sa photo (texte du produit par défaut,
   * `DEFAULT_CUSTOMER_PHOTO_NOTICE`) et sa version (incrémentée à chaque modification, enregistrée avec l'accord).
   */
  customerPhotoNotice: z.string(),
  customerPhotoNoticeVersion: z.int(),
  /** Vague 6 : durée de conservation des dossiers en années (3 à 10, défaut 5), affichée dans la politique. */
  dossierRetentionYears: z.int(),
  /** Vague 6 (ajout 10) : liste de contrôle de la fiche de réception (liste par défaut si non réglée). */
  entryChecklist: z.array(EntryChecklistItem),
  /** Vague 6 (ajout 11) : e-mails de rendez-vous au client (confirmation, rappel la veille) ; désactivé par défaut. */
  notifyPlannedVisit: z.boolean(),
  /**
   * Vague 7 : seuil du retard (jours à l'atelier, 1 à 90, défaut 7) des indicateurs de priorité
   * (`interventionUrgency`). Les modules activables (agenda, bilan hebdomadaire…) ont leur propre route
   * (`modules.list`, `modules.update`).
   */
  lateAfterDays: z.int(),
  /**
   * 02/10/2026 : seuil « sans action » (jours entiers depuis la dernière action du dossier, 1 à 60, défaut 3) :
   * `InterventionSummary.idle`, filtre `interventions.list?idle=1`. Obligatoire depuis l'intégration de la vague 7.
   */
  idleAfterDays: z.int(),
  /** 03/10/2026 (Gestion) : mois de début de l'exercice fiscal (1 = janvier … 12), défaut 1. */
  fiscalYearStartMonth: z.int().min(1).max(12),
  /** 08/10/2026 : taux horaire HT de la main-d'œuvre (catalogue de prestations), `null` s'il n'est pas réglé. */
  laborHourlyRateHt: amount(),
  /**
   * 08/10/2026 : « Le mécanicien peut envoyer les travaux complémentaires sans validation ». `null` : pas
   * encore choisi, la valeur proposée est `mechanicSendSuggested` (petit atelier). Le droit effectif reste
   * l'interrupteur `canSendWithoutValidation` de chaque membre : choisir une valeur l'applique aux mécaniciens.
   */
  mechanicSendWithoutValidation: z.boolean().nullable(),
  /** Valeur proposée : `true` quand le garage compte au plus `SMALL_WORKSHOP_MAX_MEMBERS` membres actifs. */
  mechanicSendSuggested: z.boolean(),
  /** Mécaniciens actifs, et ceux d'entre eux qui peuvent envoyer sans validation. */
  mechanicsCount: z.int(),
  mechanicsAllowedToSend: z.int(),
  /**
   * 08/10/2026 : rappel de prochaine échéance (module `maintenance_reminders`) : nombre de jours avant la date
   * d'échéance où l'e-mail part (1 à 180, défaut 30) et phrase d'accroche facultative du garage (300 caractères).
   */
  maintenanceReminderLeadDays: z.int(),
  maintenanceReminderIntro: z.string().nullable(),
});
export type SettingsOutput = z.infer<typeof SettingsOutput>;

export const SettingsUpdateInput = z.strictObject({
  storageFreeDays: z.int().min(0).max(365).optional(),
  storageDayType: StorageDayType.optional(),
  storageStartRule: StorageStartRule.optional(),
  storageDailyFee: Money.nullable().optional(),
  storageFeeTaxMode: TaxMode.optional(),
  storageTermsText: LongText.optional(),
  invoicingDraftKind: InvoicingDraftKind.optional(),
  timezone: z
    .string()
    .refine((tz) => {
      try {
        new Intl.DateTimeFormat('fr-FR', { timeZone: tz });
        return true;
      } catch {
        return false;
      }
    }, 'Fuseau horaire inconnu')
    .optional(),
  defaultVatRate: VatRate.optional(),
  portalTokenTtlDays: z.int().min(1).max(365).optional(),
  portalAccessAfterReleaseHours: z.int().min(0).max(24 * 90).optional(),
  reportLinkTtlDays: z.int().min(30).max(3650).optional(),
  notificationEmails: z.array(Email).max(10).optional(),
  countryCode: CountryCode.optional(),
  showVehiclePhotoOnPortal: z.boolean().optional(),
  /** `null` : texte par défaut du produit. */
  customerPhotoNotice: CustomerPhotoNotice.nullable().optional(),
  /** Le lien du compte rendu ne peut pas durer plus longtemps que la conservation des dossiers (422). */
  dossierRetentionYears: DossierRetentionYears.optional(),
  /** `null` : liste par défaut du produit (`DEFAULT_ENTRY_CHECKLIST`). */
  entryChecklist: EntryChecklist.nullable().optional(),
  notifyPlannedVisit: z.boolean().optional(),
  lateAfterDays: z.int().min(1).max(90).optional(),
  idleAfterDays: z.int().min(1).max(60).optional(),
  fiscalYearStartMonth: z.int().min(1).max(12).optional(),
  /** 08/10/2026 : `null` efface le taux horaire. */
  laborHourlyRateHt: Money.nullable().optional(),
  /** 08/10/2026 : appliqué à l'interrupteur `canSendWithoutValidation` des mécaniciens actifs et invités. */
  mechanicSendWithoutValidation: z.boolean().optional(),
  maintenanceReminderLeadDays: z.int().min(1).max(180).optional(),
  /** `null` ou vide : aucune phrase d'accroche. */
  maintenanceReminderIntro: z.string().trim().max(300).nullable().optional(),
});

export const LocationOutput = z.object({
  id: Id,
  name: z.string(),
  sortOrder: z.int(),
  archived: z.boolean(),
  /** Vague 7 : `area` (zone, parking), `lift` (pont) ou `bay` (poste) ; ponts et postes = ressources de l'agenda. */
  kind: LocationKind,
});
export type LocationOutput = z.infer<typeof LocationOutput>;
export const LocationCreateInput = z.strictObject({
  name: ShortText,
  sortOrder: z.int().min(0).optional(),
  kind: LocationKind.optional(),
});
export const LocationUpdateInput = z.strictObject({
  name: ShortText.optional(),
  kind: LocationKind.optional(),
  sortOrder: z.int().min(0).optional(),
  archived: z.boolean().optional(),
});
